import React, { useEffect, useState } from "react";
import {
  Plus,
  Edit,
  GraduationCapIcon,
  Trash,
  Users,
  User,
  CreditCard,
  MessageSquare,
  QrCode,
  CloudUpload,
  IndianRupee,
  Home,
  Briefcase,
  Upload,
  FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../config/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { BorderAllRounded, Event, Payment } from "@mui/icons-material";
import { getUserRoles, hasPermission, PERMISSIONS } from "../utils/roleAccess";

export default function ManagerComponent() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [canAccessDashboard, setCanAccessDashboard] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          setUserData(userData);
          setUserRoles(getUserRoles(userData));
          setCanAccessDashboard(
            hasPermission(userData, PERMISSIONS.ADMIN_DASHBOARD),
          );
        } else {
          setUserData(null);
          setUserRoles([]);
          setCanAccessDashboard(false);
        }
      } else {
        setUser(null);
        setUserData(null);
        setUserRoles([]);
        setCanAccessDashboard(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  const handleNavigation = (path, isActive = true) => {
    if (isActive) {
      navigate(path);
    }
  };

  const isFullAdmin = userRoles.includes("admin");
  const isItPerson = userRoles.includes("it");
  const isPrincipal = userRoles.includes("principal");

  const cardOptions = [
    {
      icon: <Plus className="w-8 h-8 mb-2" />,
      label: "Add User",
      path: "/add-user",
      active: true,
    },
    {
      icon: <MessageSquare className="w-8 h-8 mb-2" />,
      label: "Notice Board",
      path: "/notice-board",
      active: true,
    },
    {
      icon: <CreditCard className="w-8 h-8 mb-2" />,
      label: "View Payments",
      path: "/view-payments",
      active: true,
    },
    {
      icon: <GraduationCapIcon className="w-8 h-8 mb-2" />,
      label: "Update Student",
      path: "/update-student",
      active: true,
    },
    {
      icon: <BorderAllRounded className="w-8 h-8 mb-2" />,
      label: "Student Excel Manager",
      path: "/student-excel-manager",
      active: true,
    },
    {
      icon: <GraduationCapIcon className="w-8 h-8 mb-2" />,
      label: "Class Promotion",
      path: "/class-promotion-manager",
      active: true,
    },
    {
      icon: <QrCode className="w-8 h-8 mb-2" />,
      label: "Onboard Students",
      path: "/student-onbarding",
      active: true,
    },
    // to add new qr codes
    // {
    //   icon: <QrCode className="w-8 h-8 mb-2" />,
    //   label: "Admissions QR Bulk",
    //   path: "/admissions-qr-bulk",
    //   active: true,
    // },
    // maage exsitng qr code
    // {
    //   icon: <QrCode className="w-8 h-8 mb-2" />,
    //   label: "Student QR Manager",
    //   path: "/student-qr-manager",
    //   active: true,
    // },
    {
      icon: <Briefcase className="w-8 h-8 mb-2" />,
      label: "Vacancies Manager",
      path: "/manage-vacancies",
      active: true,
    },
    {
      icon: <Event className="w-8 h-8 mb-2" />,
      label: "Event Manager",
      path: "/manage-events",
      active: true,
    },
    {
      icon: <Upload className="w-8 h-8 mb-2" />,
      label: "Upload Circular",
      path: "/manage-circulars",
      active: true,
    },
    {
      icon: <FileText className="w-8 h-8 mb-2" />,
      label: "Disclosure Documents",
      path: "/mandatory-public-disclosure",
      active: true,
    },
    {
      icon: <Home className="w-8 h-8 mb-2" />,
      label: "Home Carousel",
      path: "/manage-home-carousel",
      active: true,
    },
    {
      icon: <CloudUpload className="w-8 h-8 mb-2" />,
      label: "Media & Updates Manager",
      path: "/manage-gallery-images",
      active: true,
    },
    {
      icon: <Payment className="w-8 h-8 mb-2" />,
      label: "Admin Dashboard",
      path: "/manage-dashboard",
      active: true,
    },
    {
      icon: <Edit className="w-8 h-8 mb-2" />,
      label: "Edit User",
      path: "/edit-user",
    },
    {
      icon: <Trash className="w-8 h-8 mb-2" />,
      label: "Delete User",
      path: "/delete-user",
    },
    {
      icon: <Users className="w-8 h-8 mb-2" />,
      label: "View Users",
      path: "/view-users",
    },
    {
      icon: <User className="w-8 h-8 mb-2" />,
      label: "Parent Meeting",
      path: "/parent-meeting",
    },
  ];

  const roleCardOptions = {
    it: [
      {
        icon: <GraduationCapIcon className="w-8 h-8 mb-2" />,
        label: "Update Student",
        path: "/update-student",
        active: true,
      },
      {
        icon: <QrCode className="w-8 h-8 mb-2" />,
        label: "Onboard Students",
        path: "/student-onbarding",
        active: true,
      },
      {
        icon: <Upload className="w-8 h-8 mb-2" />,
        label: "Manage Circular",
        path: "/manage-circulars",
        permission: PERMISSIONS.MANAGE_CIRCULARS,
        active: true,
      },
      {
        icon: <FileText className="w-8 h-8 mb-2" />,
        label: "Disclosure Documents",
        path: "/mandatory-public-disclosure",
        active: true,
      },
      {
        icon: <Event className="w-8 h-8 mb-2" />,
        label: "Add New Event",
        path: "/add-event",
        permission: PERMISSIONS.CREATE_EVENTS,
        active: true,
      },
      {
        icon: <MessageSquare className="w-8 h-8 mb-2" />,
        label: "Notice Board",
        path: "/manage-notices",
        permission: PERMISSIONS.MANAGE_NOTICES,
        active: true,
      },
    ],
    principal: [
      {
        icon: <MessageSquare className="w-8 h-8 mb-2" />,
        label: "Notice Board",
        path: "/manage-notices",
        permission: PERMISSIONS.MANAGE_NOTICES,
        active: true,
      },
      {
        icon: <Event className="w-8 h-8 mb-2" />,
        label: "Add New Event",
        path: "/add-event",
        permission: PERMISSIONS.CREATE_EVENTS,
        active: true,
      },
      {
        icon: <CreditCard className="w-8 h-8 mb-2" />,
        label: "View Payments",
        path: "/view-payments",
        permission: PERMISSIONS.VIEW_PAYMENTS,
        active: true,
      },
      {
        icon: <Upload className="w-8 h-8 mb-2" />,
        label: "Manage Circular",
        path: "/manage-circulars",
        permission: PERMISSIONS.MANAGE_CIRCULARS,
        active: true,
      },
    ],
  };

  const roleVisibleCardOptions = isFullAdmin
    ? cardOptions
    : isPrincipal
      ? roleCardOptions.principal
      : isItPerson
        ? roleCardOptions.it
        : cardOptions.filter((item) =>
            ["/student-onbarding", "/admissions-qr-bulk"].includes(item.path),
          );
  const visibleCardOptions = roleVisibleCardOptions.filter(
    (item) => !item.permission || hasPermission(userData, item.permission),
  );
  const hasRoleSpecificDashboard = !isFullAdmin && (isItPerson || isPrincipal);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-500">
          You are not authorized to view this page.
        </p>
      </div>
    );
  }

  if (!user) {
    navigate("/");
    return (
      <p className="text-lg text-gray-600 font-medium mb-4">
        You are not authorized to view this page.
      </p>
    );
  }

  if (!canAccessDashboard) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <p className="text-lg text-gray-600 font-medium mb-4">
          You are not authorized to view this page.
        </p>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition"
        >
          Login
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      <div className="bg-blue-800 text-white text-lg font-semibold p-4 rounded-2xl shadow-md text-center mb-6">
        {isFullAdmin
          ? "Admin Panel"
          : isPrincipal
            ? "Principal Dashboard"
            : isItPerson
              ? "IT Person Dashboard"
              : "Onboarding Panel"}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {visibleCardOptions.map((item, index) => {
          const isActive = item.active || false;
          return (
            <Card
              key={index}
              onClick={() => handleNavigation(item.path, isActive)}
              className={`shadow-md cursor-pointer transition transform hover:scale-105 ${
                isActive
                  ? "bg-white hover:shadow-xl"
                  : "bg-gray-200 cursor-not-allowed opacity-60"
              }`}
              style={{ borderRadius: "16px" }}
            >
              <CardContent className="flex flex-col items-center justify-center p-6 text-center">
                <div
                  className={`${isActive ? "text-purple-700" : "text-gray-500"}`}
                >
                  {item.icon}
                </div>
                <p
                  className={`text-sm font-medium ${
                    isActive ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {item.label}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {!hasRoleSpecificDashboard && (
        <div className="mb-20">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Quick Actions
            </h2>
            <p className="text-sm text-gray-500">
              {isFullAdmin
                ? "Shortcuts for frequently used admin tasks"
                : "Quick access for student onboarding"}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <button
              type="button"
              onClick={() => handleNavigation("/student-onbarding", true)}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 p-5 text-left text-white shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <span className="absolute right-0 top-0 h-20 w-20 translate-x-6 -translate-y-6 rounded-full bg-white/15 blur-md" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center mb-4">
                  <GraduationCapIcon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold">Student Onboarding</h3>
                <p className="text-sm text-emerald-50 mt-1 mb-4">
                  Open students with inactive QR status and send them into the
                  onboarding form.
                </p>
                <span className="inline-flex items-center rounded-lg bg-white text-teal-700 px-3 py-1.5 text-sm font-semibold group-hover:bg-teal-50 transition-colors">
                  Open Queue
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleNavigation("/admissions-qr-bulk", true)}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 p-5 text-left text-white shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <span className="absolute right-0 top-0 h-20 w-20 translate-x-6 -translate-y-6 rounded-full bg-white/15 blur-md" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center mb-4">
                  <QrCode className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold">Admissions QR Bulk</h3>
                <p className="text-sm text-amber-100 mt-1 mb-4">
                  Upload all admission QR images in one run and auto-create
                  missing students as inactive.
                </p>
                <span className="inline-flex items-center rounded-lg bg-white text-amber-700 px-3 py-1.5 text-sm font-semibold group-hover:bg-amber-50 transition-colors">
                  Open Bulk Upload
                </span>
              </div>
            </button>

            {/* <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 p-5 shadow-sm">
            <div className="w-11 h-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-4">
              <QrCode className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-700">
              Update Student QR
            </h3>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              Upload, replace, and remove student QR images from Firebase.
            </p>
            <button
              type="button"
              onClick={() => handleNavigation("/student-qr-manager", true)}
              className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
            >
              Open QR Manager
            </button>
          </div> */}

            {isFullAdmin ? (
              <button
                type="button"
                onClick={() => handleNavigation("/update-payment", true)}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-700 via-indigo-700 to-purple-700 p-5 text-left text-white shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <span className="absolute right-0 top-0 h-20 w-20 translate-x-6 -translate-y-6 rounded-full bg-white/15 blur-md" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center mb-4">
                    <IndianRupee className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">Update Payment</h3>
                  <p className="text-sm text-blue-100 mt-1 mb-4">
                    Modify payment details and publish instantly.
                  </p>
                  <span className="inline-flex items-center rounded-lg bg-white text-indigo-700 px-3 py-1.5 text-sm font-semibold group-hover:bg-blue-50 transition-colors">
                    Open Manager
                  </span>
                </div>
              </button>
            ) : null}

            {isFullAdmin ? (
              <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 p-5 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-4">
                  <CloudUpload className="w-5 h-5 text-gray-500" />
                </div>
                <h3 className="text-base font-semibold text-gray-700">
                  Upload Circular
                </h3>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Circular upload quick action is disabled here.
                </p>
                <button
                  type="button"
                  disabled
                  className="w-full py-2.5 rounded-lg bg-gray-200 text-gray-500 text-sm font-medium cursor-not-allowed"
                >
                  Use Circular Module
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
